'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '@/components/sections/kit';
import { routePath } from '@/lib/routes';
import {
  NAV,
  SHOW_ROADMAP,
  docsPath,
  etaTag,
  findBySlug,
  flatVisible,
  iaStamp,
} from './data/nav';
import { API } from './data/api';
import { LOGIN_URL } from '@/lib/app-url';

/**
 * The /docs chrome: sticky topbar, always-visible nav tree (findability is
 * the product — no collapsing), and the ⌘K search modal whose index derives
 * from the nav tree plus every endpoint in the reference. Below 1000px the
 * sidebar drops out (mobile drawer is flagged out of scope this round).
 */

interface SearchEntry {
  id: string;
  label: string;
  sub: string;
  tag: string | null;
  hay: string;
}

// The index is static data — build it once at module scope.
const SEARCH_INDEX: SearchEntry[] = [
  ...flatVisible()
    .filter((i) => i.id !== 'home')
    .map((i) => ({
      id: i.id,
      label: i.label,
      sub: i.group,
      tag: etaTag(i.eta),
      hay: `${i.label} ${i.group} ${i.kws}`.toLowerCase(),
    })),
  ...Object.entries(API).flatMap(([id, page]) =>
    page.blocks
      .filter((b) => b.method)
      .map((b) => ({
        id,
        label: `${b.method} ${b.path}`,
        sub: `${page.label.toUpperCase()} — ${b.t.toUpperCase()}`,
        tag: null,
        hay: `${b.method} ${b.path} ${b.t} ${page.label}`.toLowerCase(),
      })),
  ),
];

const searchResults = (q: string): SearchEntry[] => {
  const query = q.trim().toLowerCase();
  if (!query) return SEARCH_INDEX.slice(0, 8);
  return SEARCH_INDEX.filter((r) => r.hay.includes(query)).slice(0, 9);
};

const SearchContext = createContext<() => void>(() => {});

/** Opens the ⌘K modal — used by the topbar chip and the home search field. */
export const useOpenSearch = () => useContext(SearchContext);

const AMBER_TAG =
  'rounded-full border border-[color-mix(in_srgb,var(--color-data-amber)_45%,transparent)] bg-[color-mix(in_srgb,var(--color-data-amber)_14%,white)] px-1.5 py-0.5 font-jetbrains text-[8.5px] tracking-[0.05em] text-[color-mix(in_srgb,var(--color-data-amber)_62%,var(--color-ink))]';

export default function DocsShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  // /en/docs/api/links → 'api/links' (active nav item). The locale prefix is
  // always present in the App Router path; docs links themselves stay
  // un-prefixed like the rest of the site (proxy.ts adds the locale back).
  const slug = pathname.replace(/^\/[^/]+\/docs\/?/, '');
  const active = findBySlug(slug)?.id ?? 'home';

  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [kbd, setKbd] = useState('⌘K');
  const inputRef = useRef<HTMLInputElement>(null);

  // The drawer never outlives a navigation (#238) — and it locks body
  // scroll while open so the page under it doesn't wander.
  useEffect(() => setDrawerOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!/mac/i.test(navigator.platform)) setKbd('Ctrl K');
  }, []);

  const openSearch = useCallback(() => {
    setOpen(true);
    setQ('');
    setSel(0);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const results = useMemo(() => searchResults(q), [q]);

  const go = useCallback(
    (id: string) => {
      setOpen(false);
      router.push(docsPath(id));
    },
    [router],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        setQ('');
        setSel(0);
        return;
      }
      if (!open) {
        if (e.key === 'Escape') setDrawerOpen(false);
        return;
      }
      if (e.key === 'Escape') setOpen(false);
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel((s) => Math.min(s + 1, Math.max(results.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => Math.max(s - 1, 0));
      } else if (e.key === 'Enter') {
        const r = results[sel];
        if (r) go(r.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, sel, go]);

  const groups = NAV.map((g) => ({
    label: g.group,
    items: g.items.filter((i) => SHOW_ROADMAP || i.status !== 'roadmap'),
  })).filter((g) => g.items.length);

  // One tree render, two mounts: the desktop sidebar and the mobile drawer
  // (#238) — same derived data, amber roadmap tags included.
  const navTree = (
    <>
      {groups.map((g) => (
        <div key={g.label} className="mt-[22px]">
          <div className="px-2.5 pb-[7px] font-jetbrains text-[10px] tracking-[0.14em] text-dim">
            {g.label}
          </div>
          <div className="flex flex-col gap-px">
            {g.items.map((i) => {
              const isActive = i.id === active;
              return (
                <Link
                  key={i.id}
                  href={docsPath(i.id)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13.5px] transition-colors ${
                    isActive
                      ? 'bg-[color-mix(in_srgb,var(--color-brand-600)_8%,white)] font-[650] text-brand-700'
                      : 'font-medium text-muted hover:bg-surface-2 hover:text-ink'
                  }`}
                >
                  <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                    {i.label}
                  </span>
                  {etaTag(i.eta) && i.status === 'roadmap' && (
                    <span className={`ml-auto flex-none ${AMBER_TAG}`}>{etaTag(i.eta)}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
      <div className="mt-[34px] border-t border-line px-2.5 pt-3 font-jetbrains text-[9px] leading-[1.8] tracking-[0.1em] text-dim">
        {iaStamp()}
      </div>
    </>
  );

  return (
    <SearchContext.Provider value={openSearch}>
      <div className="min-h-screen bg-bg-warm text-ink antialiased">
        {/* ─── Topbar ─── */}
        <header className="sticky top-0 z-40 border-b border-line-2 bg-[color-mix(in_srgb,var(--color-bg-warm)_86%,transparent)] backdrop-blur-[8px]">
          <div className="mx-auto flex h-14 max-w-[1460px] items-center gap-3.5 px-6">
            <button
              type="button"
              onClick={() => setDrawerOpen((o) => !o)}
              aria-expanded={drawerOpen}
              aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
              className="hidden h-[33px] w-[33px] flex-none items-center justify-center rounded-[9px] border border-line-2 bg-surface text-muted transition-colors hover:text-ink max-[999px]:flex"
            >
              {drawerOpen ? <CloseIcon size={15} /> : <MenuIcon size={15} />}
            </button>
            <Link href="/docs" className="flex items-center gap-3">
              <span className="font-onest text-lg font-extrabold tracking-[-0.02em] text-ink">
                rii<span className="text-brand-600">.link</span>
              </span>
              <span className="h-4 w-px bg-line-3" />
              <span className="font-jetbrains text-[10px] tracking-[0.14em] text-muted">DOCS</span>
              {/* Hidden on the narrowest screens: with the drawer burger in
                  the row, this pill pushed the topbar 2px past a 390px
                  viewport (#238 QA). */}
              <span className="rounded-full border border-line-3 px-[7px] py-0.5 font-jetbrains text-[9px] tracking-[0.08em] text-dim max-[479px]:hidden">
                API v1
              </span>
            </Link>
            <button
              type="button"
              onClick={openSearch}
              className="ml-7 flex h-[33px] w-[250px] cursor-text items-center gap-[9px] rounded-[9px] border border-line-2 bg-surface px-2.5 text-[12.5px] text-dim transition-colors hover:border-line-3 max-[720px]:hidden"
            >
              <SearchIcon size={13} />
              <span>Search docs…</span>
              <span className="ml-auto rounded-[5px] border border-line-2 bg-surface-2 px-1.5 py-0.5 font-jetbrains text-[9.5px] text-muted">
                {kbd}
              </span>
            </button>
            <div className="ml-auto flex items-center gap-[18px]">
              {/* Small screens lose the search chip at 720px — this icon keeps
                  a search affordance everywhere (#238). */}
              <button
                type="button"
                onClick={openSearch}
                aria-label="Search docs"
                className="hidden h-[33px] w-[33px] flex-none items-center justify-center rounded-[9px] border border-line-2 bg-surface text-muted transition-colors hover:text-ink max-[720px]:flex"
              >
                <SearchIcon size={14} />
              </button>
              {/* Not a live indicator — a link to the real, Kuma-backed status page. */}
              <a
                href={routePath('status')}
                className="inline-flex items-center gap-[7px] font-jetbrains text-[9.5px] tracking-[0.1em] text-muted transition-colors hover:text-ink max-[900px]:hidden"
              >
                API STATUS ↗
              </a>
              <a
                href={routePath('home')}
                className="text-[13px] font-[550] text-muted transition-colors hover:text-ink max-[900px]:hidden"
              >
                rii.link ↗
              </a>
              <Button
                variant="primary"
                href={LOGIN_URL}
                className="!px-4 !py-2 !text-[13.5px]"
              >
                Dashboard
              </Button>
            </div>
          </div>
        </header>

        <div className="mx-auto grid max-w-[1460px] grid-cols-[264px_minmax(0,1fr)] items-start max-[999px]:grid-cols-1">
          {/* ─── Sidebar: the full tree, always visible on desktop ─── */}
          <aside className="sticky top-[57px] flex h-[calc(100vh-57px)] flex-col overflow-y-auto border-r border-line-2 px-3.5 pb-7 pt-4 max-[999px]:hidden">
            {navTree}
          </aside>

          <main className="min-w-0">{children}</main>
        </div>

        {/* ─── Mobile nav drawer (#238): same tree, below-1000px only ─── */}
        {drawerOpen && (
          <div
            className="fixed inset-0 top-[57px] z-[70] hidden max-[999px]:block"
            onClick={() => setDrawerOpen(false)}
          >
            <div className="absolute inset-0 bg-[color-mix(in_srgb,var(--color-ink)_28%,transparent)] backdrop-blur-[2px]" />
            <nav
              aria-label="Documentation"
              className="absolute bottom-0 left-0 top-0 w-[300px] max-w-[86vw] overflow-y-auto border-r border-line-2 bg-bg-warm px-3.5 pb-8 pt-1 shadow-[20px_0_60px_-30px_rgba(16,16,40,.4)]"
              onClick={(e) => e.stopPropagation()}
            >
              {navTree}
            </nav>
          </div>
        )}

        {/* ─── ⌘K search modal ─── */}
        {open && (
          <div
            className="fixed inset-0 z-[80] flex items-start justify-center bg-[color-mix(in_srgb,var(--color-ink)_32%,transparent)] px-5 pt-[13vh] backdrop-blur-[3px]"
            onClick={() => setOpen(false)}
          >
            <div
              className="w-[600px] max-w-full overflow-hidden rounded-2xl border border-line-3 bg-surface shadow-[0_30px_80px_-30px_rgba(16,16,40,.45)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-[11px] border-b border-line-2 px-[18px] py-3.5">
                <span className="text-dim">
                  <SearchIcon size={16} />
                </span>
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setSel(0);
                  }}
                  placeholder="Search docs, endpoints, error codes…"
                  className="flex-1 border-none bg-transparent text-[15px] text-ink outline-none"
                />
                <span className="rounded-[5px] border border-line-2 bg-surface-2 px-[7px] py-0.5 font-jetbrains text-[9.5px] text-muted">
                  ESC
                </span>
              </div>
              <div className="max-h-[380px] overflow-y-auto p-2">
                {results.map((r, i) => (
                  <button
                    key={`${r.id}-${r.label}`}
                    type="button"
                    onClick={() => go(r.id)}
                    onMouseEnter={() => setSel(i)}
                    className={`flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left ${
                      i === sel ? 'bg-surface-2' : ''
                    }`}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13.5px] font-semibold text-ink">
                        {r.label}
                      </span>
                      <span className="font-jetbrains text-[9.5px] tracking-[0.06em] text-dim">
                        {r.sub}
                      </span>
                    </span>
                    {r.tag && <span className={`ml-auto flex-none ${AMBER_TAG}`}>{r.tag}</span>}
                    {i === sel && (
                      <span className={`flex-none font-jetbrains text-[11px] text-dim ${r.tag ? '' : 'ml-auto'}`}>
                        ↵
                      </span>
                    )}
                  </button>
                ))}
                {results.length === 0 && (
                  <div className="px-4 py-[26px] text-center text-[13.5px] text-muted">
                    Nothing for “{q}”. Try an endpoint path — “POST /v1/links” — or a resource name.
                  </div>
                )}
              </div>
              <div className="flex gap-4 border-t border-line px-[18px] py-2.5 font-jetbrains text-[9px] tracking-[0.1em] text-dim">
                <span>↑↓ NAVIGATE</span>
                <span>↵ OPEN</span>
                <span>ESC CLOSE</span>
                <span className="ml-auto">
                  INDEX DERIVES FROM THE NAV TREE · {SEARCH_INDEX.length} ENTRIES
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </SearchContext.Provider>
  );
}

function MenuIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

function CloseIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M5 5l14 14M19 5L5 19" />
    </svg>
  );
}

function SearchIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4.2-4.2" />
    </svg>
  );
}
